import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Tabs,
    Tab,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import BadgeCard from './BadgeCard';
import { gamificationService, BadgeProgress, UserBadge } from '../../services/gamificationService';

interface BadgeGridProps {
    showTabs?: boolean;
    title?: string;
}

const BadgeGrid: React.FC<BadgeGridProps> = ({ showTabs = true, title }) => {
    const translate = useTranslate();
    const [tab, setTab] = useState(0);
    const [earnedBadges, setEarnedBadges] = useState<UserBadge[]>([]);
    const [badgeProgress, setBadgeProgress] = useState<BadgeProgress[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [earned, progress] = await Promise.all([
                    gamificationService.getMyBadges(),
                    gamificationService.getBadgeProgress(),
                ]);
                setEarnedBadges(earned);
                setBadgeProgress(progress);
            } catch (error) {
                console.error('Failed to load badges:', error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    const unearnedBadges = badgeProgress.filter(b => !b.isEarned);

    const renderBadges = (badges: (BadgeProgress | UserBadge)[]) => (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 2,
                mt: 2,
            }}
        >
            {badges.map((badge) => (
                <BadgeCard
                    key={'badgeId' in badge ? badge.badgeId : badge.id}
                    badge={badge}
                />
            ))}
        </Box>
    );

    return (
        <Box>
            {title && (
                <Typography variant="h6" gutterBottom>
                    {title}
                </Typography>
            )}

            {showTabs ? (
                <>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                        <Tab label={`${translate('gamification.earned')} (${earnedBadges.length})`} />
                        <Tab label={`${translate('gamification.available')} (${unearnedBadges.length})`} />
                    </Tabs>

                    {tab === 0 && (
                        earnedBadges.length > 0 ? (
                            renderBadges(earnedBadges)
                        ) : (
                            <Typography color="text.secondary" sx={{ mt: 2 }}>
                                {translate('gamification.noBadgesYet')}
                            </Typography>
                        )
                    )}

                    {tab === 1 && (
                        unearnedBadges.length > 0 ? (
                            renderBadges(unearnedBadges)
                        ) : (
                            <Typography color="text.secondary" sx={{ mt: 2 }}>
                                {translate('gamification.allBadgesEarned')}
                            </Typography>
                        )
                    )}
                </>
            ) : (
                renderBadges(earnedBadges)
            )}
        </Box>
    );
};

export default BadgeGrid;
